// @ts-check
import { gql } from '@apollo/client';
import groupBy from 'lodash/groupBy';
import React from 'react';
import { Registration, Text } from '../../../pdf-design-system';
import { usePage } from '../../../pdf-design-system/Base/PageLayout/Page';
import getStands from '../getStands';
import { findRoomByStandName, rooms } from '../helpers/rooms';
import { RoomProvider } from '../useRoom';
import RoomCover from './RoomCover';
import RoomPlan from './RoomPlan';
import StaticCatalogPageContent from './StaticCatalogPageContent';
import VieVinumPageFrame from './VieVinumPageFrame';
import VieVinumTable from './VieVinumTable';

/**
 * @param {{
 * data: import('./@types/VieVinumRooms').VieVinumRoomsFragment;
 * bleed?: number;
 * }} props
 * @returns
 */
export default function VieVinumRooms({ data, bleed, ...rest }) {
  const stands = afterQuery(data, rest);
  const groups = Object.values(groupBy(stands, 'group.name')).map((stands) => ({
    stands,
    group: stands[0].group,
    room: stands[0].room,
  }));
  return (
    <>
      <Room
        room={rooms.find((room) => room.slug === 'halle')}
        stands={[]}
        bleed={bleed}
        {...rest}
      />
      {groups.map(({ room, stands, group }) =>
        group.type === 'room' ? (
          <Room
            key={group.key}
            room={room}
            stands={stands}
            bleed={bleed}
            {...rest}
          />
        ) : (
          <File
            key={group.key}
            files={
              /** @type {Extract<StandOrFile, {__typename: "UploadedFileCatalogSection"}>[]} */ (
                stands
              )
            }
            room={room}
            bleed={bleed}
            {...rest}
          />
        )
      )}
    </>
  );
}

/**
 *
 * @param {{
 *  room: typeof rooms[number];
 *  stands: StandOrFile[];
 *  bleed?: number;
 * }} props
 * @returns
 */
function Room({ room, stands, bleed, ...rest }) {
  return (
    <RoomProvider value={room || /** @type {typeof rooms[number]} */ ({})}>
      <VieVinumPageFrame bleed={bleed}>
        <RoomCover />
      </VieVinumPageFrame>
      {!room?.hidePlan && (
        <VieVinumPageFrame bleed={bleed}>
          <RoomPlan />
        </VieVinumPageFrame>
      )}
      {/* The PageFrame needs to be outside of the SinglePage because it sets the page alignment. */}
      {Array.from(Array(Math.ceil(stands.length / 4))).map((_, index) => (
        <VieVinumPageFrame key={index} bleed={bleed}>
          <SinglePage
            stands={stands.slice(index * 4, index * 4 + 4)}
            {...rest}
          />
        </VieVinumPageFrame>
      ))}
    </RoomProvider>
  );
}

/**
 *
 * @param {{
 *  room: typeof rooms[number];
 *  files: Extract<StandOrFile, {__typename: "UploadedFileCatalogSection"}>[];
 *  bleed?: number;
 * }} props
 * @returns
 */
function File({ room, files, bleed }) {
  return (
    <RoomProvider value={room || /** @type {typeof rooms[number]} */ ({})}>
      {files.map((file) => (
        <VieVinumPageFrame key={file.filename} bleed={bleed}>
          <StaticCatalogPageContent>
            <Text style={{ textAlign: 'center', paddingTop: '100mm' }}>
              {file.filename}
            </Text>
          </StaticCatalogPageContent>
        </VieVinumPageFrame>
      ))}
    </RoomProvider>
  );
}

/** @param {{ stands: StandOrFile[] }} props */
function SinglePage({ stands }) {
  const { alignment } = usePage();
  return (
    <StaticCatalogPageContent>
      {stands.map((section, index) => {
        const style =
          alignment === 'left'
            ? { paddingRight: '6mm' }
            : { paddingLeft: '6mm' };
        if (section.__typename === 'RegistrationCatalogSection') {
          return (
            <Registration.Provider
              key={section.key}
              value={section.registration}
            >
              <VieVinumTable style={style} />
            </Registration.Provider>
          );
        }
        if (section.__typename === 'UploadedFileCatalogSection') {
          return <React.Fragment key={section.filename}></React.Fragment>;
        }
        return <React.Fragment key={index}></React.Fragment>;
      })}
    </StaticCatalogPageContent>
  );
}

const parse = gql;
/** @type {import('@apollo/client').TypedDocumentNode<import('./@types/VieVinumRooms').VieVinumRoomsFragment>}*/
VieVinumRooms.fragment = parse/* GraphQL */ `
  fragment VieVinumRooms on CatalogConfiguration {
    catalogConfigurationId
    sections {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          isLocked
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...VieVinumTable
    }
  }

  ${VieVinumTable.fragment}
`;

/** @typedef {ReturnType<afterQuery>[number]} StandOrFile */

/**
 * @param {import('./@types/VieVinumRooms').VieVinumRoomsFragment} data
 * @param {{ companyId?: string; registrationId?: string; }} rest
 */
function afterQuery(data, rest) {
  const sections = getSections(data, rest);
  /** @type {import('../helpers/rooms').Room | undefined} */
  let currentRoom = undefined;

  const stands = sections
    .map((section) => {
      if (section.__typename === 'UploadedFileCatalogSection') {
        return {
          ...section,
          room: currentRoom,
          group: {
            ...currentRoom,
            name: section.filename,
            key: section.filename,
            type: 'file',
          },
        };
      }
      currentRoom = findRoomByStandName(section.registration?.stand?.name);
      const stands = getStands(
        section,
        section.registration.registeredProducts.nodes
      );
      return stands.map((stand) => ({
        ...stand,
        group: {
          ...stand.room,
          key: stand.room?.slug || '-',
          type: 'room',
        },
      }));
    })
    .flat();
  return stands;
}

/** @typedef {ReturnType<getSections>[number]} Section */

/**
 * @param {import('./@types/VieVinumRooms').VieVinumRoomsFragment} data
 * @param {{ companyId?: string; registrationId?: string; isLocked?: string; }} params
 */
function getSections(data, params) {
  if (params.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === params.companyId
    );
  }
  if (params.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === params.registrationId
    );
  }
  if (params.isLocked != null) {
    const isLocked = params.isLocked === 'true' ? true : false;
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.isLocked === isLocked
    );
  }
  return data.sections;
}
